import { toast } from 'src/components/ui/toast';
import { ReceiveCostAdjust } from './ReceiveCostAdjust';
import { useRef, useState, type KeyboardEvent } from 'react';
import { useGT } from 'gt-react';
import {
  PpvReceiveAtSchema,
  type LineBuyTerms,
  type PpvReceiveAt,
} from '@erp/shared-types';
import { cn } from '@/lib/utils';
import {
  useSetLineBuyTerms,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { isBuyDesignated } from '../utils/lineFilters';
import { parseRevisedCost, ppvDeltaPerUnit } from '../utils/lineBuyTerms';
import { isReleasedStatus } from '../utils/releasedStatus';
import { formatUnitCost } from './unitCostFormat';
import { useLineBulkEditTargets } from './lineBulkEditContext';
import { RevisedCostAdjust } from './RevisedCostAdjust';
import { idBatches } from '../utils/idBatches';

const INPUT_CLASS =
  'h-6 w-full min-w-0 rounded border border-transparent bg-transparent px-1 text-xs hover:border-input focus:border-input focus:bg-background focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50';

function Dash() {
  return <span className="block text-right text-muted-foreground">—</span>;
}

/**
 * Saves a terms patch on the line, or on every line of a shift-highlight.
 * `disabled` while saving, and always on a released line: its terms are on
 * the order already, and the server saves terms on pending lines only.
 */
function useSaveTerms(line: AssignmentLine) {
  const gt = useGT();
  const editTargets = useLineBulkEditTargets();
  const { mutateAsync } = useSetLineBuyTerms();
  // Expanded need rows can put more lines in one edit than a call takes. The
  // mutation's isPending follows only its latest call, so the edit counts
  // itself saving until every batch settles.
  const [saving, setSaving] = useState(0);
  const save = (terms: Partial<LineBuyTerms>) => {
    setSaving((count) => count + 1);
    const batches = idBatches([...editTargets(line.suggestionId)]);
    void Promise.allSettled(
      batches.map((suggestionIds) => mutateAsync({ suggestionIds, terms })),
    )
      .then((results) => {
        if (results.some((result) => result.status === 'rejected')) {
          toast.error(
            gt(
              'Could not save the buy terms. Check that the receive cost stays between the system and PO costs, then try again.',
              { context: 'buyer board buy terms save error' },
            ),
          );
        }
      })
      .finally(() => setSaving((count) => count - 1));
  };
  return { save, disabled: saving > 0 || isReleasedStatus(line.status) };
}

/**
 * An inline text input that saves on Enter or blur and reverts on Escape.
 * Mounted with the saved value as its key, so a saved change from elsewhere
 * resets the draft.
 */
function InlineInput({
  saved,
  label,
  disabled,
  inputMode,
  align = 'left',
  onCommit,
}: {
  saved: string;
  label: string;
  disabled: boolean;
  inputMode?: 'decimal';
  align?: 'left' | 'right';
  /** Return false to drop a draft that is not valid, reverting it. */
  onCommit: (draft: string) => boolean | void;
}) {
  const [draft, setDraft] = useState(saved);
  // Escape reverts and blurs; the blur that follows must not save the draft.
  const cancelled = useRef(false);
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    if (draft.trim() === saved.trim()) return;
    if (onCommit(draft) === false) setDraft(saved);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Keep board shortcuts (j/k, approval keys) out of a field being typed in.
    event.stopPropagation();
    if (event.key === 'Enter') event.currentTarget.blur();
    if (event.key === 'Escape') {
      cancelled.current = true;
      setDraft(saved);
      event.currentTarget.blur();
    }
  };
  return (
    <input
      type="text"
      value={draft}
      aria-label={label}
      title={draft || label}
      inputMode={inputMode}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        INPUT_CLASS,
        align === 'right' && 'text-right tabular-nums',
      )}
    />
  );
}

/** Buyer cost: the system (list) unit cost, before contracts and breaks. */
export function BuyerCostCell({ line }: { line: AssignmentLine }) {
  if (line.buyerUnitCost === null || !isBuyDesignated(line)) return <Dash />;
  return (
    <span className="block text-right text-xs tabular-nums">
      {formatUnitCost(line.buyerUnitCost)}
    </span>
  );
}

/**
 * The buyer's one-time special cost; empty clears it. With the order's lines,
 * it also offers the adjust popover (percentage, set cost, reset).
 */
export function RevisedCostCell({
  line,
  getOrderLines,
}: {
  line: AssignmentLine;
  getOrderLines?: () => readonly AssignmentLine[];
}) {
  const gt = useGT();
  const { save, disabled } = useSaveTerms(line);
  if (!isBuyDesignated(line)) return <Dash />;
  const revised = line.buyTerms.revisedUnitCost;
  const saved = revised === null ? '' : revised.toFixed(4);
  const input = (
    <InlineInput
      key={saved}
      saved={saved}
      label={gt('Revised cost', {
        context: 'buyer board line input: one-time special unit cost',
      })}
      disabled={disabled}
      inputMode="decimal"
      align="right"
      onCommit={(draft) => {
        const revisedUnitCost = parseRevisedCost(draft);
        if (revisedUnitCost === undefined) return false;
        save({ revisedUnitCost });
      }}
    />
  );
  if (!getOrderLines) return input;
  return (
    <div className="flex items-center gap-0.5">
      {input}
      <RevisedCostAdjust
        line={line}
        getOrderLines={getOrderLines}
        disabled={disabled}
      />
    </div>
  );
}

/** Variance per unit: green and positive below the buyer cost, red and negative above. */
export function PpvDeltaCell({ line }: { line: AssignmentLine }) {
  const delta = isBuyDesignated(line) ? ppvDeltaPerUnit(line) : null;
  if (delta === null || delta === 0) return <Dash />;
  return (
    <span
      className={cn(
        'block text-right text-xs tabular-nums',
        delta > 0 ? 'text-emerald-700' : 'text-red-700',
      )}
    >
      {delta > 0 ? '+' : ''}
      {formatUnitCost(delta)}
    </span>
  );
}

/** A free-text term: the PO contract number or the PO line comment. */
export function BuyTermTextCell({
  line,
  field,
  label,
}: {
  line: AssignmentLine;
  field: 'contractNumber' | 'poLineComment';
  label: string;
}) {
  const { save, disabled } = useSaveTerms(line);
  if (!isBuyDesignated(line)) return <Dash />;
  const saved = line.buyTerms[field] ?? '';
  return (
    <InlineInput
      key={saved}
      saved={saved}
      label={label}
      disabled={disabled}
      onCommit={(draft) => save({ [field]: draft.trim() || null })}
    />
  );
}

const PPV_NONE = '';

/** Receive at PO cost, buyer cost, or a saved amount between the two. */
export function PpvCell({
  line,
  getOrderLines,
}: {
  line: AssignmentLine;
  getOrderLines?: () => readonly AssignmentLine[];
}) {
  const gt = useGT();
  const { save, disabled } = useSaveTerms(line);
  if (!isBuyDesignated(line)) return <Dash />;
  const options: { value: PpvReceiveAt | typeof PPV_NONE; label: string }[] = [
    { value: PPV_NONE, label: '—' },
    {
      value: 'po_cost',
      label: gt('P', {
        context:
          'buyer board PPV flag option, short: receive at the PO cost. Keep to one letter',
      }),
    },
    {
      value: 'buyer_cost',
      label: gt('B', {
        context:
          'buyer board PPV flag option, short: receive at the buyer (system) cost. Keep to one letter',
      }),
    },
    {
      value: 'cost_constraint',
      label: gt('C', {
        context:
          'buyer board PPV flag: cost constraint cost, keep to one letter',
      }),
    },
  ];
  return (
    <div className="flex items-center gap-1">
      <select
        value={line.buyTerms.ppvReceiveAt ?? PPV_NONE}
        disabled={disabled}
        aria-label={gt(
          'Receive basis: PO cost (P), buyer cost (B), or cost constraint cost (C)',
          {
            context: 'buyer board line PPV flag select',
          },
        )}
        title={gt(
          'Receive at PO cost (P), buyer cost (B), or cost constraint cost (C)',
          {
            context: 'buyer board line PPV flag select tooltip',
          },
        )}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onChange={(event) => {
          const value = event.target.value;
          save({
            ppvReceiveAt: PpvReceiveAtSchema.safeParse(value).data ?? null,
          });
        }}
        className={cn(INPUT_CLASS, 'w-10 shrink-0 cursor-pointer')}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {line.buyTerms.ppvReceiveAt === 'cost_constraint' && (
        <span className="text-xs tabular-nums">
          {line.buyTerms.costConstraintUnitCost ?? '—'}
        </span>
      )}
      <ReceiveCostAdjust
        line={line}
        getOrderLines={getOrderLines ?? (() => [line])}
        disabled={disabled}
      />
    </div>
  );
}
